import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Modal from "~/components/ui/modal";
import { TOAST_MESSAGES } from "~/constants/messages";
import { KnowledgeApi } from "~/functions/api/knowledge";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import ArrowBackIcon from "~/images/arrow-left_black.svg?react";
import EditIcon from "~/images/edit_white.svg?react";
import UserIcon from "~/images/user.svg?react";
import type { Knowledge } from "~/interface/knowledge";
import type { RootState } from "~/store";
import CreateKnowledgeModal, { type CreateKnowledgeModalRef } from "./CreateKnowledgeModal";
import styles from "./KnowledgeDetail.module.scss";
import pendingStyles from "./PendingKnowledgeModal.module.scss";

// ドキュメントタイプの日本語変換
const getDocumentTypeLabel = (type: string): string => {
  const typeMap: { [key: string]: string } = {
    "system-proposal": "システム概要書",
    "functional-requirements": "機能要件書",
    "non-functional-requirements": "非機能要件書",
    "plugin-proposal": "プラグイン提案",
    "app-design": "アプリ設計",
    "field-design": "フィールド設計書",
    "customize-specification": "カスタマイズ仕様書",
  };
  return typeMap[type] || type;
};

// Unixタイムスタンプを日付に変換するユーティリティ
const parseTimestamp = (timestamp: string | number): Date | null => {
  try {
    // 文字列の場合は数値に変換
    const numericTimestamp = typeof timestamp === "string" ? parseFloat(timestamp) : timestamp;

    // Unixタイムスタンプ（秒単位）の場合
    if (numericTimestamp > 1000000000 && numericTimestamp < 10000000000) {
      return new Date(numericTimestamp * 1000);
    }

    // ミリ秒単位のタイムスタンプの場合
    if (numericTimestamp > 1000000000000) {
      return new Date(numericTimestamp);
    }

    // ISO文字列として試行
    const date = new Date(timestamp);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }

    return null;
  } catch (error) {
    console.error("Error parsing timestamp:", error);
    return null;
  }
};

// 日付を日本語形式でフォーマット
const formatDateForDisplay = (timestamp: string | number): string => {
  const parsedDate = parseTimestamp(timestamp);
  if (parsedDate) {
    return parsedDate.toLocaleString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return "不明";
};

const KnowledgeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [knowledge, setKnowledge] = useState<Knowledge | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const editModalRef = React.useRef<CreateKnowledgeModalRef>(null);
  const [editFooterState, setEditFooterState] = useState<{
    canSubmit: boolean;
    isSubmitting: boolean;
  }>({ canSubmit: false, isSubmitting: false });

  const { currentUser, currentProject, projectMembers } = useSelector(
    (state: RootState) => state.auth,
  ) as any;
  const { canManageKnowledge, canViewKnowledge } = useProjectPermission();
  const { isLoading: isProjectMembersLoading, error: projectMembersError } = useProjectMembers();
  const knowledgeApi = new KnowledgeApi();

  // projectMembersのロード状態を判定
  // useProjectMembers フックは currentProject?.id が利用可能な場合に fetchProjectData を実行する
  // フェッチが完了すると isProjectMembersLoading が false になり、projectMembers にデータが入る
  const hasProjectMembersData = Array.isArray(projectMembers) && projectMembers.length > 0;

  useEffect(() => {
    if (id && currentUser?.organization_id && currentUser?.id && currentProject?.id) {
      fetchKnowledge();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchKnowledge is intentionally excluded to prevent infinite loop, dependencies are checked inside the effect
  }, [id, currentUser?.organization_id, currentUser?.id, currentProject?.id]);

  const fetchKnowledge = async () => {
    if (!id || !currentUser?.organization_id || !currentUser?.id || !currentProject?.id) {
      setError("必要なパラメーターが不足しています");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 承認済みのナレッジ一覧から該当するものを探す
      const approvedData = await knowledgeApi.getByStatus(
        currentProject.id,
        currentUser.organization_id,
        "approved",
      );

      let targetKnowledge: Knowledge | null = null;

      if (approvedData) {
        // 配列の場合とオブジェクトの場合を処理
        const dataArray = Array.isArray(approvedData) ? approvedData : [approvedData];
        targetKnowledge = dataArray.find((item: Knowledge) => item.id === id) || null;
      }

      // 承認済みデータで見つからない場合は、承認待ちデータも確認
      if (!targetKnowledge) {
        const pendingData = await knowledgeApi.getByStatus(
          currentProject.id,
          currentUser.organization_id,
          "pending",
        );

        if (pendingData) {
          const pendingArray = Array.isArray(pendingData) ? pendingData : [pendingData];
          targetKnowledge = pendingArray.find((item: Knowledge) => item.id === id) || null;
        }
      }

      if (targetKnowledge) {
        setKnowledge(targetKnowledge);
      } else {
        setError("ナレッジが見つかりません");
      }
    } catch (error) {
      console.error("Failed to fetch knowledge:", error);
      setError("ナレッジの取得に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // パンくずの「ナレッジ」クリックで一覧へ戻る
  const handleBreadcrumbToList = () => {
    if (currentUser?.organization_id && currentProject?.id) {
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/knowledge`);
    } else {
      navigate(-1);
    }
  };

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  // 編集モーダルを閉じる関数
  const closeEditModal = () => {
    setEditModalOpen(false);
  };

  // ナレッジ更新後の処理
  const handleKnowledgeUpdated = (updatedKnowledge: Knowledge) => {
    // 詳細ページの表示データを更新
    setKnowledge(updatedKnowledge);

    // 編集モーダルを閉じる
    closeEditModal();
  };

  const handleToggleActive = async () => {
    if (!knowledge || !currentUser?.id || !currentUser?.organization_id || !currentProject?.id)
      return;

    const newStatus = !knowledge.isActive;

    try {
      const result = await knowledgeApi.updateIsActive(
        currentUser.organization_id,
        currentProject.id,
        knowledge.id,
        !knowledge.isActive,
      );

      // APIが成功した場合（trueが返された場合）
      if (result === true) {
        setKnowledge((prev) => (prev ? { ...prev, isActive: newStatus } : null));

        // 成功時のtoast通知
        const statusText = newStatus ? "有効" : "無効";
        toast.success(TOAST_MESSAGES.KNOWLEDGE.STATUS_CHANGED(statusText));
      } else {
        toast.error(TOAST_MESSAGES.KNOWLEDGE.STATUS_UPDATE_FAILED);
      }
    } catch (error) {
      console.error("Failed to update knowledge status:", error);
      toast.error(TOAST_MESSAGES.KNOWLEDGE.STATUS_UPDATE_ERROR);
    }
  };

  if (isLoading) {
    return (
      <div className={styles["container"]}>
        <div className={styles["loading"]}>読み込み中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles["container"]}>
        <div className={styles["error"]}>{error}</div>
      </div>
    );
  }

  if (!knowledge) {
    return (
      <div className={styles["container"]}>
        <div className={styles["error"]}>ナレッジが見つかりません</div>
      </div>
    );
  }

  // URL直接アクセス時、projectMembersのロード完了を待機
  // isProjectMembersLoading が true の間、またはデータがまだ取得されていない間はローディング表示
  // currentProject?.id が存在し、projectMembers が空の場合はまだフェッチ中の可能性がある
  const shouldWaitForProjectMembers =
    isProjectMembersLoading ||
    (currentProject?.id && !hasProjectMembersData && !projectMembersError);

  if (shouldWaitForProjectMembers) {
    return (
      <div className={styles["container"]}>
        <div className={styles["loading"]}>読み込み中...</div>
      </div>
    );
  }

  // projectMembersの取得に失敗した場合のエラー表示
  if (projectMembersError) {
    return (
      <div className={styles["container"]}>
        <div className={styles["error"]}>プロジェクト情報の取得に失敗しました</div>
      </div>
    );
  }

  // 権限チェック: guestは表示不可
  if (!canViewKnowledge()) {
    return (
      <div className={styles["container"]}>
        <div className={styles["header"]}>
          <button className={styles["backButton"]} onClick={() => navigate(-1)}>
            <ArrowBackIcon />
            戻る
          </button>
        </div>
        <div className={styles["content"]}>
          <div className={styles["emptyState"]}>
            <p>ナレッジを表示する権限がありません。</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["container"]}>
      <div className={styles["header"]}>
        <div className={styles["breadcrumb"]}>
          <span
            className={styles["breadcrumbItem"]}
            role="button"
            tabIndex={0}
            onClick={handleBreadcrumbToList}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleBreadcrumbToList();
              }
            }}
          >
            ナレッジ
          </span>
          <span className={styles["breadcrumbSeparator"]}>＞</span>
          <span className={styles["breadcrumbItem"]}>詳細</span>
        </div>

        <div className={styles["titleSection"]}>
          <div className={styles["titleLeft"]}>
            <button className={styles["backButton"]} onClick={handleBack}>
              <ArrowBackIcon />
            </button>
            <h1 className={styles["title"]}>{knowledge.title || "無題"}</h1>
          </div>
          {canManageKnowledge() && (
            <div className={styles["titleRight"]}>
              <button className={styles["editButton"]} onClick={handleEdit}>
                <EditIcon />
                編集
              </button>
            </div>
          )}
        </div>

        <div className={styles["dateInfo"]}>
          <div className={styles["createdDate"]}>
            作成日: {knowledge.created_at ? formatDateForDisplay(knowledge.created_at) : "不明"}
          </div>
          {knowledge.modified_at && (
            <div className={styles["modifiedDate"]}>
              更新日: {formatDateForDisplay(knowledge.modified_at)}
            </div>
          )}
        </div>
      </div>

      <div className={styles["content"]}>
        <div className={styles["section"]}>
          <h2 className={styles["sectionTitle"]}>プロンプト</h2>
          <div className={styles["promptContent"]}>
            {knowledge.meta_prompt || "プロンプトが設定されていません"}
          </div>
        </div>

        <div className={styles["section"]}>
          <h2 className={styles["sectionTitle"]}>対象ドキュメント</h2>
          <div className={styles["documentType"]}>{getDocumentTypeLabel(knowledge.type)}</div>
        </div>

        <div className={styles["section"]}>
          <h2 className={styles["sectionTitle"]}>作成者</h2>
          <div className={styles["creator"]}>
            <span className={styles["creatorName"]}>
              <UserIcon className={styles["icon-user"]} />
              {knowledge.modified_by || "不明"}
            </span>
          </div>
        </div>

        {canManageKnowledge() && (
          <div className={styles["section"]}>
            <h2 className={styles["sectionTitle"]}>有効にする</h2>
            <div className={styles["toggleSection"]}>
              <label className={styles["switch"]}>
                <input type="checkbox" checked={knowledge.isActive} onChange={handleToggleActive} />
                <span className={styles["slider"]}></span>
              </label>
            </div>
          </div>
        )}
      </div>

      <Modal
        label={`ナレッジの編集`}
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        className="knowledgePendingModal"
        content={
          <CreateKnowledgeModal
            ref={editModalRef}
            onKnowledgeCreated={handleKnowledgeUpdated}
            onClose={closeEditModal}
            selectedType={knowledge?.type}
            editingKnowledge={knowledge}
            isEditMode={true}
            showActiveToggle={true}
            onStateChange={setEditFooterState}
          />
        }
        footer={
          <div className={pendingStyles["closeButtonBottom"]}>
            <div>
              <button
                className={pendingStyles["closeButtonBottomBtn"]}
                onClick={() => editModalRef.current?.cancel()}
                disabled={editFooterState.isSubmitting}
              >
                閉じる
              </button>
            </div>
            <div className={pendingStyles["footerActions"]}>
              <button
                className={pendingStyles["saveButton"]}
                onClick={() => editModalRef.current?.submit()}
                disabled={!editFooterState.canSubmit || editFooterState.isSubmitting}
              >
                {editFooterState.isSubmitting ? "更新中..." : "保存"}
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default KnowledgeDetail;
